import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import Card from '../../components/Card.jsx'
import TipBox from '../../components/TipBox.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

const ARCHETYPES = ['Protagonist','Antagonist','Mentor','Ally','Love interest','Trickster','Guardian','Shadow']

const EMPTY_FORM = { name:'', age:'', role:'', want:'', need:'', wound:'', contradiction:'', voice:'', arc:'' }

export default function Characters({ pid = 'w' }) {
  const [chars, setChars]       = useState([])
  const [project, setProject]   = useState({})
  const [showForm, setShowForm] = useState(false)

  useEffect(() => {
    api.getCharacters(pid).then(d => setChars(d || []))
    api.getWritingProjectData(pid).then(d => setProject(d || {}))
  }, [pid])
  const [editingId, setEditingId] = useState(null)
  const [form, setForm]         = useState(EMPTY_FORM)
  const [confirmId, setConfirmId] = useState(null)

  const setField = (f, v) => setForm(prev => ({ ...prev, [f]: v }))

  function openNew() {
    setForm(EMPTY_FORM)
    setEditingId(null)
    setShowForm(true)
  }

  function openEdit(char) {
    setForm({ ...EMPTY_FORM, ...char })
    setEditingId(char.id)
    setShowForm(true)
  }

  function saveChar() {
    if (!form.name.trim()) return
    const next = editingId
      ? chars.map(c => c.id === editingId ? { ...form, id: editingId } : c)
      : [...chars, { ...form, id: Date.now() }]
    setChars(next)
    api.saveCharacters(pid, next)
    setShowForm(false)
    setEditingId(null)
    setForm(EMPTY_FORM)
  }

  function deleteChar(id) {
    setConfirmId(id)
  }

  function confirmDeleteChar() {
    const next = chars.filter(c => c.id !== confirmId)
    setChars(next)
    api.saveCharacters(pid, next)
    setConfirmId(null)
  }

  return (
    <Card label="Section 5" title="Characters">
      <TipBox title="💡 Writing believable people" collapsible>
        <p>Great characters have a <strong>want</strong> (what they think they need), a <strong>need</strong> (what they actually need), and a <strong>wound</strong> (what shaped them). The gap between want and need creates drama. Their wound explains their blind spots.</p>
        <ul>
          <li><strong>Contradiction</strong> — the most interesting characters contain opposing truths (brave but dishonest, kind but selfish)</li>
          <li><strong>Voice</strong> — each character should speak differently, think differently, notice different things</li>
          <li><strong>Change arc</strong> — how does this character end the story differently than they began it?</li>
        </ul>
      </TipBox>

      {/* Character cards grid */}
      {chars.length > 0 && (
        <div className="char-grid" style={{ marginTop: '1.25rem' }}>
          {chars.map(c => (
            <div key={c.id} className="char-card" onClick={() => openEdit(c)}>
              <button className="char-delete-btn" onClick={e => { e.stopPropagation(); deleteChar(c.id) }}>✕</button>
              <p className="char-name">{c.name || 'Unnamed'}</p>
              <p className="char-role">{c.role || ''}</p>
              <p className="char-snippet">{c.want || c.wound || 'No notes yet'}</p>
            </div>
          ))}
        </div>
      )}

      <button className="ghost-btn" onClick={openNew} style={{ marginTop: '1.25rem', marginBottom: showForm ? '1.25rem' : 0 }}>
        + Add character
      </button>

      {/* Character form */}
      {showForm && (
        <div className="char-form-wrap">
          <p style={{ fontFamily: 'var(--font-serif)', fontSize: '1rem', color: 'var(--ink)', marginBottom: '1rem' }}>
            {editingId ? `Editing: ${form.name || 'character'}` : 'New character'}
          </p>

          {/* Archetype chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '0.75rem' }}>
            {ARCHETYPES.map(a => (
              <span
                key={a}
                className={`archetype-chip ${form.role === a ? 'active' : ''}`}
                onClick={() => setField('role', form.role === a ? '' : a)}
              >
                {a}
              </span>
            ))}
          </div>

          <div className="w-form">
            <div>
              <label className="w-label">Name</label>
              <input className="w-input" placeholder="Character name" value={form.name} onChange={e => setField('name', e.target.value)} />
            </div>
            <div>
              <label className="w-label">Age / era</label>
              <input className="w-input" placeholder="e.g. 34, or 'middle-aged'" value={form.age} onChange={e => setField('age', e.target.value)} />
            </div>
            <div className="full">
              <label className="w-label">What they want (surface goal)</label>
              <input className="w-input" placeholder="e.g. To find her missing sister" value={form.want} onChange={e => setField('want', e.target.value)} />
            </div>
            <div className="full">
              <label className="w-label">What they need (deeper truth)</label>
              <input className="w-input" placeholder="e.g. To forgive herself for surviving" value={form.need} onChange={e => setField('need', e.target.value)} />
            </div>
            <div className="full">
              <label className="w-label">Their wound (formative experience)</label>
              <input className="w-input" placeholder="e.g. She witnessed her father's breakdown and had to become the adult" value={form.wound} onChange={e => setField('wound', e.target.value)} />
            </div>
            <div className="full">
              <label className="w-label">Key contradiction (two opposing truths about them)</label>
              <input className="w-input" placeholder="e.g. Deeply empathetic but pushes everyone away" value={form.contradiction} onChange={e => setField('contradiction', e.target.value)} />
            </div>
            <div className="full">
              <label className="w-label">Voice & mannerisms (how they speak, move, think)</label>
              <textarea className="w-textarea" rows={2}
                placeholder="e.g. Speaks in short sentences. Notices exits in every room. Cracks jokes when frightened..."
                value={form.voice} onChange={e => setField('voice', e.target.value)}
              />
            </div>
            <div className="full">
              <label className="w-label">Arc (how do they change?)</label>
              <textarea className="w-textarea" rows={2}
                placeholder="e.g. Begins closed off and certain; ends uncertain but open to connection..."
                value={form.arc} onChange={e => setField('arc', e.target.value)}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="save-btn ready" onClick={saveChar}>Save character</button>
            <button className="ghost-btn" onClick={() => setShowForm(false)}>Cancel</button>
          </div>

          <TipBox title="Deepening your characters" collapsible>
            <ul>
              <li>What does your character want on the surface? What do they actually need underneath that?</li>
              <li>What is the lie your character believes about themselves or the world at the start?</li>
              <li>What would your character never do — and what would it take for the story to push them to do it anyway?</li>
              <li>How does your character speak differently to different people? To authority, to strangers, to someone they love?</li>
            </ul>
          </TipBox>
        </div>
      )}
      <ConfirmDialog
        open={confirmId !== null}
        title="Remove this character?"
        message="This cannot be undone."
        confirmLabel="Remove"
        onConfirm={confirmDeleteChar}
        onCancel={() => setConfirmId(null)}
      />
    </Card>
  )
}
